import React, { useContext } from 'react'
import classNames from 'classnames'
import _ from 'lodash'
import { Tooltip, Position } from '@blueprintjs/core'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { Avatar } from 'browser/components/atomic-elements/atoms/avatar/avatar'
import { HelpBlock } from 'browser/components/atomic-elements/atoms/help-block/help-block'
import { Label } from 'browser/components/atomic-elements/atoms/label/label'

import { AppNavigatorContext } from 'browser/contexts/app-navigator/app-navigator-context'
import { InternalViewContext } from 'browser/contexts/internal-view/internal-view-context'
import { Owner } from 'shared-libs/generated/server-types/entity'
import {
  renderAuthorHeader,
  renderEventTimestamp,
  renderDistance,
  renderDeviceInfoTooltipContent,
} from '../util'
import { renderEventChange, EventChanges, EventMapping } from './event-changes'
import { EventType } from 'shared-libs/models/types/storyboard/storyboard-execution'
import { Event } from 'shared-libs/models/types/storyboard/storyboard-execution-model'

type TaskGroupMeta = {
  key?: string
  scope?: 'task' | 'scene'
  storyId?: string
  sceneId?: string
  taskId?: string
  eventTypes?: EventType[]
  events?: any[]
}

interface IEditItemProps extends IBaseProps {
  event: any
  owner: Owner
  index: number
  isFirst: boolean
  isLast: boolean
  workflowEntity: any
  appointmentDestinationPath?: string
}

export const EditItem: React.FC<IEditItemProps> = (props) => {
  const { className, appointmentDestinationPath, event, isFirst, owner, workflowEntity } = props
  const { createdBy, createdAt, creationDate, processedDate } = event
  const { settings } = useContext(AppNavigatorContext)
  const { showInternals } = useContext(InternalViewContext)

  const plan = _.get(
    workflowEntity,
    'core_storyboard_execution.storyboardPlan.core_storyboard_plan'
  )

  if (_.isEmpty(plan)) {
    return null
  }

  const pathId = event.source && (event.source.taskId || event.source.sceneId)
  const pathName = plan.getNameFromPathId(pathId)

  const changeElements = (event.outputMappings || [])
    .map((mapping: EventMapping, index: number) =>
      renderEventChange({ mapping, workflowEntity: workflowEntity, index })
    )
    .filter(Boolean)

  const taskGroupSummary =
    event.eventType === EventType.TASK_GROUP
      ? renderTaskGroupSummary(event.taskGroup as TaskGroupMeta | undefined, pathName)
      : null
  const notificationChanges =
    event.eventType === EventType.TASK_GROUP
      ? renderTaskNotificationChanges(event.taskGroup as TaskGroupMeta | undefined)
      : []
  const combinedChangeElements = _.compact([...changeElements, ...notificationChanges])

  if (_.isEmpty(combinedChangeElements) && !showInternals && !taskGroupSummary) {
    return null
  }

  return (
    <div className={classNames('c-timelineEvent', className)} key={event.uniqueId}>
      <div
        className={classNames('c-timelineEvent-line', className, {
          'c-timelineEvent-line--topCutOff': isFirst,
        })}
      />
      <div className="c-timelineEvent-dot" />
      <div className="c-timelineEvent-contentContainer">
        <div className="c-timelineEvent-header">
          <div className="c-timelineEvent-author">
            <Avatar className="c-timelineEvent-avatar" name={createdBy.displayName} size="xs" />
            <Tooltip
              disabled={!event.deviceInfo}
              content={renderDeviceInfoTooltipContent(event.deviceInfo) ?? undefined}
              position={Position.TOP}
            >
              <Label className="c-timelineEvent-authorName">
                {renderAuthorHeader(createdBy, owner?.firm)}
              </Label>
            </Tooltip>
          </div>
          <HelpBlock className="c-timelineEvent-timestamp">
            {renderEventTimestamp(creationDate, processedDate, showInternals)}
          </HelpBlock>
        </div>
        <div className="c-timelineEvent-body">
          {renderSubheading(event, pathName)}
          {taskGroupSummary}
          <EventChanges>{combinedChangeElements}</EventChanges>
        </div>
        <div className="c-timelineEvent-footer">
          {createdAt &&
            renderDistance(event, workflowEntity, 'miles', settings, appointmentDestinationPath)}
        </div>
      </div>
    </div>
  )
}

function renderSubheading(event: Event, pathName: string | undefined) {
  const overrideText = getEventSpecificSubheading(event)

  if (overrideText) {
    return <p>{overrideText}</p>
  }

  if (!pathName) {
    return <p>Updated the workflow</p>
  }

  return (
    <p>
      Completed <strong>{pathName}</strong>
    </p>
  )
}

function renderTaskGroupSummary(taskGroup?: TaskGroupMeta | null, pathName?: string) {
  const eventTypes = taskGroup?.eventTypes || []
  if (_.isEmpty(eventTypes)) {
    return null
  }

  const summaryParts: string[] = []
  const scopeLabel = taskGroup?.scope === 'scene' ? 'scene' : 'task'
  const label =
    pathName ||
    prettifyIdentifier(taskGroup?.taskId || taskGroup?.sceneId) ||
    _.startCase(scopeLabel)

  if (eventTypes.includes(EventType.BREADCRUMB)) {
    summaryParts.push(`Viewed this ${scopeLabel}`)
  }

  if (eventTypes.includes(EventType.TRAILER_LOOKUP_RESPONSE)) {
    summaryParts.push('Trailer lookup response')
  }

  if (eventTypes.includes(EventType.MAP_APPOINTMENT)) {
    summaryParts.push('Mapped appointment data')
  }

  if (!summaryParts.length) {
    return (
      <p className="c-timelineEvent-taskGroupSummary">
        <strong>{label}</strong>
      </p>
    )
  }

  return (
    <p className="c-timelineEvent-taskGroupSummary">
      <strong>{label}</strong>
      <span> — {summaryParts.join(' • ')}</span>
    </p>
  )
}

function renderTaskNotificationChanges(taskGroup?: TaskGroupMeta | null) {
  const events = taskGroup?.events || []
  return events
    .filter((groupEvent) =>
      [EventType.NOTIFICATION, EventType.NOTIFICATION_LOG].includes(groupEvent?.eventType)
    )
    .map((notification, index) => renderNotificationChange(notification, index))
    .filter(Boolean)
}

function renderNotificationChange(notificationEvent: Event, index: number) {
  const message = formatNotificationMessage(notificationEvent)
  const receivers = formatNotificationReceivers(notificationEvent)
  const channels = formatNotificationChannels(notificationEvent)

  if (!message && !receivers) {
    return null
  }

  const createdByName = _.get(notificationEvent, 'createdBy.displayName', 'User')
  const messageLabel = message ? <strong>{message}</strong> : 'a notification'

  return (
    <div key={`${notificationEvent?.id || index}-notification`}>
      {createdByName} sent {messageLabel}
      {receivers && (
        <>
          {' '}
          to <strong>{receivers}</strong>
        </>
      )}
      {channels && <> via {channels}</>}
    </div>
  )
}

function formatNotificationMessage(event: Event): string | undefined {
  const userDataEvent = event?.outputMappings?.find(
    (mapping: { name?: unknown } | null | undefined) => mapping?.name === 'User data event'
  )
  const userDataMessage = _.get(userDataEvent, 'value.message')

  return (
    userDataMessage ||
    _.get(event, 'details.name') ||
    _.get(event, 'details.notification.template.displayName')
  )
}

function formatNotificationReceivers(event: Event): string | undefined {
  const receivers = _.get(event, 'details.notification.receivers')
  if (!receivers || !receivers.length) {
    return undefined
  }

  const names = receivers
    .map((receiver: unknown) => _.get(receiver, 'deferredUser.user.displayName'))
    .filter(Boolean)

  return names.length ? names.join(', ') : undefined
}

function formatNotificationChannels(event: Event): string | undefined {
  const types = _.get(event, 'details.notification.types')
  if (!types || !types.length) {
    return undefined
  }

  return types.join(', ')
}

function getEventSpecificSubheading(event: Event): string | undefined {
  switch (event?.eventType) {
    case EventType.MAP_APPOINTMENT:
      return 'Mapped appointment data'
    case EventType.TRAILER_LOOKUP_RESPONSE:
      return 'Processed trailer lookup response'
    default:
      return undefined
  }
}

function prettifyIdentifier(identifier?: string) {
  if (!identifier) {
    return undefined
  }

  const cleaned = identifier
    .replace(/^scene_/, '')
    .replace(/^task_/, '')
    .replace(/^story_/, '')
    .replace(/\./g, ' ')
    .replace(/_/g, ' ')

  return _.startCase(cleaned)
}
